/**
 * Salary Advance Approval Flow Configuration
 *
 * Single source of truth for grade → approval chain routing.
 * Change grades or flows here without touching any component code.
 */

export type FlowId = 'A' | 'B' | 'C';
export type ApprovalLevelNumber = 1 | 2 | 3 | 4 | 5 | 6;

// ─── Grade → Flow mapping ─────────────────────────────────────────────────────
//
//  Flow A (4 steps): General Services, Professional, Supervisory (S1–S3), Technical (T1–T3)
//    L1 (Country HR) → L2 (Country Manager) → L3 (Global HR) → L4 (Group CFO)
//
//  Flow B (1 step):  Middle & Senior Management (M1–M4), Technical (T4)
//    L5 (EXCO) only
//
//  Flow C (1 step):  Executive
//    L6 (CEO) only
//
export const GRADE_TO_FLOW: Record<string, FlowId> = {
  // General Services & Internships
  G:  'A',
  // Professional
  P1: 'A',
  P2: 'A',
  P3: 'A',
  // Supervisory
  S1: 'A',
  S2: 'A',
  S3: 'A',
  // Technical
  T1: 'A',
  T2: 'A',
  T3: 'A',
  T4: 'B',
  // Middle Management
  M1: 'B',
  M2: 'B',
  // Senior Management
  M3: 'B',
  M4: 'B',
  // Executive
  E1: 'C',
};

// ─── Flow → ordered approval level list ──────────────────────────────────────
export const FLOW_STEPS: Record<FlowId, ApprovalLevelNumber[]> = {
  A: [1, 2, 3, 4],
  B: [5],
  C: [6],
};

// ─── Approval level display metadata ─────────────────────────────────────────
export const APPROVAL_LEVEL_DISPLAY: Record<
  ApprovalLevelNumber,
  { label: string; roleHint: string }
> = {
  1: { label: 'Approval Level 1', roleHint: 'Country HR' },
  2: { label: 'Approval Level 2', roleHint: 'Country Manager' },
  3: { label: 'Approval Level 3', roleHint: 'Global HR' },
  4: { label: 'Approval Level 4', roleHint: 'Group CFO' },
  5: { label: 'Approval Level 5', roleHint: 'EXCO' },
  6: { label: 'Approval Level 6', roleHint: 'CEO' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Returns the ordered list of approval levels for the given grade.
 * Returns [] for unknown/null grades — callers should handle gracefully.
 */
export function getApprovalSteps(grade: string | undefined | null): ApprovalLevelNumber[] {
  if (!grade) return [];
  const flow = GRADE_TO_FLOW[grade.toUpperCase() as string];
  if (!flow) {
    console.warn(`[salaryAdvanceFlows] Unknown grade "${grade}" — no approval flow assigned.`);
    return [];
  }
  return FLOW_STEPS[flow];
}

/**
 * Returns true when the approver at `approverLevel` is allowed to act on a request.
 * For multi-step flows all PREVIOUS levels must already have action === 'approved'.
 * For single-step flows (B, C) there are no prerequisites.
 */
export function canApproverAct(
  grade: string,
  approverLevel: ApprovalLevelNumber,
  approvals: { level: number; action: string }[],
): boolean {
  const steps = getApprovalSteps(grade);
  const idx = steps.indexOf(approverLevel);
  if (idx < 0) return false;
  if (idx === 0) return true;
  const previousLevels = steps.slice(0, idx);
  return previousLevels.every(prev =>
    approvals.some(a => a.level === prev && a.action === 'approved'),
  );
}

/**
 * Determines whether an approver at `viewerLevel` is authorized to edit a salary advance request.
 * Callers must additionally ensure the request status is `pending` — approved, rejected, cancelled
 * and in-progress requests cannot be edited.
 *
 * Rules:
 * - Flow A (Multi-level: 1 -> 2 -> 3 -> 4): Approver Level 1 (Country HR) has exclusive access
 *   to the "Edit Request" button. Subsequent approver levels (2, 3, 4) cannot edit.
 * - Single-level flows (Flow B: Level 5 [EXCO], Flow C: Level 6 [CEO]): Enabled for the authorized approver
 *   of that flow since the entire flow requires only one level of approval.
 */
export function canApproverEditRequest(
  grade: string | undefined | null,
  viewerLevel: ApprovalLevelNumber | null | undefined,
): boolean {
  if (!viewerLevel) return false;
  const steps = getApprovalSteps(grade || 'P1');

  // Single-level flows (Flow B: [5], Flow C: [6])
  if (steps.length === 1) {
    return steps[0] === viewerLevel;
  }

  // Multi-level flows (Flow A: [1, 2, 3, 4]):
  // Approver Level 1 (Country HR) has exclusive access to the Edit Request button
  return viewerLevel === 1;
}

/**
 * Country-scoping rule:
 *  Levels 1–3 → see only their own country's requests.
 *  Levels 4–6 → global (no country restriction).
 */
export function isApprovalLevelGlobal(level: ApprovalLevelNumber): boolean {
  return level >= 4;
}

// ─── Backend Text Parser ──────────────────────────────────────────────────────
/**
 * Canonical enum strings used by the backend.
 * The backend employee table approvalLevel column accepts any of these values.
 */
export type BackendApprovalLevelString =
  | 'APPROVAL_LEVEL_ONE'
  | 'APPROVAL_LEVEL_TWO'
  | 'APPROVAL_LEVEL_THREE'
  | 'APPROVAL_LEVEL_FOUR'
  | 'APPROVAL_LEVEL_FIVE'
  | 'APPROVAL_LEVEL_SIX';

export const BACKEND_APPROVAL_LEVEL_MAP: Record<string, ApprovalLevelNumber> = {
  // Canonical backend enum values
  APPROVAL_LEVEL_ONE: 1,
  APPROVAL_LEVEL_TWO: 2,
  APPROVAL_LEVEL_THREE: 3,
  APPROVAL_LEVEL_FOUR: 4,
  APPROVAL_LEVEL_FIVE: 5,
  APPROVAL_LEVEL_SIX: 6,

  // Level prefixes
  LEVEL_ONE: 1,
  LEVEL_TWO: 2,
  LEVEL_THREE: 3,
  LEVEL_FOUR: 4,
  LEVEL_FIVE: 5,
  LEVEL_SIX: 6,

  LEVEL_1: 1,
  LEVEL_2: 2,
  LEVEL_3: 3,
  LEVEL_4: 4,
  LEVEL_5: 5,
  LEVEL_6: 6,

  APPROVAL_LEVEL_1: 1,
  APPROVAL_LEVEL_2: 2,
  APPROVAL_LEVEL_3: 3,
  APPROVAL_LEVEL_4: 4,
  APPROVAL_LEVEL_5: 5,
  APPROVAL_LEVEL_6: 6,

  // Digit strings
  '1': 1,
  '2': 2,
  '3': 3,
  '4': 4,
  '5': 5,
  '6': 6,

  // Word only
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FOUR: 4,
  FIVE: 5,
  SIX: 6,
};

/**
 * Parses any backend approval level representation into a numeric level (1–6).
 * Handles:
 *  - "APPROVAL_LEVEL_ONE" ... "APPROVAL_LEVEL_SIX" (standard backend format)
 *  - "Approval Level One" / "Approval Level 1" / "Level 1"
 *  - "1" ... "6" or numeric 1 ... 6
 * Returns null if missing or unrecognized.
 */
export function parseApprovalLevel(raw: unknown): ApprovalLevelNumber | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'number' && raw >= 1 && raw <= 6) {
    return raw as ApprovalLevelNumber;
  }
  if (typeof raw === 'string') {
    const cleaned = raw.trim().toUpperCase().replace(/[\s-]+/g, '_');
    if (BACKEND_APPROVAL_LEVEL_MAP[cleaned]) {
      return BACKEND_APPROVAL_LEVEL_MAP[cleaned];
    }
    const num = parseInt(raw.trim(), 10);
    if (!isNaN(num) && num >= 1 && num <= 6) {
      return num as ApprovalLevelNumber;
    }
  }
  return null;
}

// ─── Employment Type Eligibility ──────────────────────────────────────────────

/**
 * Checks if the employee has Full-Time employment status.
 * Accepts various representations: 'Full-Time', 'Full Time', 'full_time', etc.
 */
export function isFullTimeEmployee(rawType?: string | null): boolean {
  if (!rawType) return false;
  const normalized = rawType.trim().toLowerCase().replace(/[\s_-]/g, '');
  return normalized === 'fulltime';
}

// ─── Salary Advance Request Type Policy ───────────────────────────────────────

export type DerivedRequestType =
  | 'Standard Request'
  | 'Management Request'
  | 'Executive Request'
  | 'Exemption'
  | 'Exception';

export interface DeriveRequestTypeInput {
  grade?: string | null;
  amount: number;
  grossSalary: number;
  tenureMonths: number;
}

export interface RequestTypeDerivation {
  requestType: DerivedRequestType;
  baseType: 'Standard Request' | 'Management Request' | 'Executive Request';
  isException: boolean;
  exceptionReasons: string[];
}

/**
 * Derives the salary advance request type according to policy:
 *
 * 1. Base Type (determined by employee grade):
 *    - General Services / Internships (G), Professional (P1, P2, P3), Supervisory (S1, S2, S3)
 *      → 'Standard Request'
 *    - Middle Management (M1, M2), Senior Management (M3, M4)
 *      → 'Management Request'
 *    - Executive (E1)
 *      → 'Executive Request'
 *
 * 2. Exemption Rule (cannot be manually chosen; overrides base type):
 *    - If requested amount > gross salary
 *    - If tenure > 3 months
 *    → 'Exemption'
 */
export function deriveSalaryAdvanceRequestType(
  input: DeriveRequestTypeInput,
): RequestTypeDerivation {
  const { grade, amount, grossSalary, tenureMonths } = input;
  const cleanGrade = (grade || '').trim().toUpperCase();

  let baseType: 'Standard Request' | 'Management Request' | 'Executive Request' = 'Standard Request';
  if (['M1', 'M2', 'M3', 'M4', 'T4'].includes(cleanGrade) || (cleanGrade.startsWith('M') && cleanGrade.length <= 3)) {
    baseType = 'Management Request';
  } else if (cleanGrade === 'E1' || (cleanGrade.startsWith('E') && cleanGrade.length <= 3)) {
    baseType = 'Executive Request';
  } else {
    baseType = 'Standard Request';
  }

  const exceptionReasons: string[] = [];

  // Exemption Trigger 1: Amount exceeds gross salary
  if (grossSalary > 0 && amount > grossSalary) {
    exceptionReasons.push(
      `Requested amount exceeds monthly gross salary`,
    );
  }

  // Exemption Trigger 2: Tenure exceeds 3 months
  if (tenureMonths > 3) {
    exceptionReasons.push(
      `Repayment tenure of ${tenureMonths} months exceeds the standard 3-month policy limit`,
    );
  }

  const isException = exceptionReasons.length > 0;
  const requestType: DerivedRequestType = isException ? 'Exemption' : baseType;

  return {
    requestType,
    baseType,
    isException,
    exceptionReasons,
  };
}

/**
 * Calculates tenure end date as an ISO datetime string given a duration in months.
 */
export function calculateTenureDate(months: number, baseDate?: Date): string {
  const date = baseDate ? new Date(baseDate) : new Date();
  date.setMonth(date.getMonth() + months);
  return date.toISOString();
}

